// External dependencies
import { DeliveryAddress, MARKETPLACE_BUSINESS_LINE, OrderApiData } from "@swiggy-private/payment-types";
import { GiftDetails, GiftReceiverDetails } from "./SwiggyMoney";

export interface ITripTag {
    attributeName: string;
    attributeType: string;
    attributeSelectedValue: string[];
    mandatoryCheck: boolean;
}

export interface IGroupOrderDetails {
    participantDetails: Array<{ participantEmailRef: string }>;
    externalGuestCount: string;
}

// Extending OrderApiData to include pending payment params
export interface OrderApiDataWithPendingPayment extends OrderApiData {
    pendingPaymentParams?: {
        method: string;
        endpoint: string;
        host: string;
        headers: { [key: string]: string };
        body?: { [key: string]: unknown };
    };
}

export interface PlaceOrderRequestParams {
    addressId: string | number;
    paymentMethod: string;
    orderType?: string;
    orderComments?: string;
    proceedWithDuplicateOrder?: boolean;
    useJuspayNative?: boolean;
    paymentReturnUrl?: string;
    cartKey?: string;
    orderId?: string | number;
    marketplaceBusinessLine?: string;
    cartType?: string;
    paymentAmount?: string | number;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    transaction_amount?: string | number;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    cvv_mandatory?: boolean;
    expenseCodeData?: {
        projectCode?: string;
        purposeOfOrdering?: string;
        tripTags?: ITripTag[];
        groupOrderDetails?: IGroupOrderDetails;
    };
    [key: string]: unknown;
}

export interface ConfirmOrderRequestParams {
    transactionId: string;
    orderId: string | number;
    marketplaceBusinessLine?: string;
    marketplaceCategory?: string;
    confirmOrderXp?: boolean;
    [key: string]: unknown;
}

// Match server-side ConfirmOrderParams exactly
export interface ConfirmOrderParams {
    [index: string]: unknown;
}

// Match server-side EditOrderConfirmParams exactly
/* eslint-disable @typescript-eslint/naming-convention */
export interface EditOrderConfirmParams extends ConfirmOrderParams {
    order_id: string;
    transaction_id: string;
    // This is optional param in downstream api. We are not using this currently.
    // TODO: check with app team why is this required and what is the usecase.
    retry_attempt?: number;
}
/* eslint-enable @typescript-eslint/naming-convention */

// Match server-side OrderContextParams exactly
export interface OrderContextParams {
    context: ORDER_CONTEXT;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    cart_key: string;
}

export enum DashOrderContexts {
    ORDER_JOB = "ORDER_JOB",
}

export enum DashPaymentTypes {
    PRE_PAYMENT = "PRE_PAYMENT",
    POST_PAYMENT = "POST_PAYMENT",
    SCENES_FULL_PAYMENT = "SCENES_FULL_PAYMENT",
}

export enum ORDER_CONTEXT {
    EDIT = "edit",
    POST_ORDER_TIPPING = "post_order_tipping",
    SWIGGY_SUPER = "swiggy_super",
    PAY_ON_DELIVERY = "pay_on_delivery",
    SWIGGY_ONE = "subscription",
}

/* eslint-disable @typescript-eslint/naming-convention */

export interface UpiTransactionInfo {
    intent_app?: string;
    intent_app_display_name?: string;
}

export interface PlaceOrderParams {
    payment_cod_method: string;
    address_id: string;
    order_comments?: string;
    order_type?: string;
    proceed_with_duplicate_order?: boolean;
    transaction_amount?: number | string;
    swiggy_pay_amount?: number | string;
    payment_method_meta?: PlaceOrderPaymentMethodMeta;
    force_validate_coupon?: boolean;
    [index: string]: unknown;
    // TODO - Cleanup required after feature
    giftDetails?: GiftDetails;
    in_app_upi_params?: InAppUpiParams;
    upi_transaction_info?: UpiTransactionInfo;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface PlaceOrderPaymentMethodMeta {
    /* eslint-disable @typescript-eslint/naming-convention */
    phonepe_web_callback_url?: string;
    amazon_pay_redirect_url?: string;
    amazon_payment_meta_data?: string;
    /** Swiggy Super contract: meta id / encrypted payload from Native Bridge GET_APAY_METADATA */
    amazon_pay_meta?: string;
    /** Swiggy Super contract: boolean flag (other BLs send `isEnoughBalanceInWallet` as string) */
    is_enough_balance_in_wallet?: boolean;
    juspay_redirect_url?: string;
    swiggy_pay_amount?: string | number;
    is_juspay_native?: string;
    cvv_mandatory?: boolean;
    mmt_meta?: {
        expense_code: string;
        purpose_of_ordering: string;
        tripTags?: ITripTag[];
        groupOrderDetails?: IGroupOrderDetails;
    };
    /* eslint-enable @typescript-eslint/naming-convention */
}

export interface InAppUpiParams {
    /* eslint-disable @typescript-eslint/naming-convention */
    account_reference_id?: string;
    account_type?: string;
    /* eslint-enable @typescript-eslint/naming-convention */
}

interface SwiggyAddMoneyPlaceParamsPaymentInfo {
    amount: number;
    paymentMethod: string;
    paymentMethodMeta: { [index: string]: unknown };
}

export interface SwiggyMoneyOrderPlaceParams {
    amount: number;
    creditMethod: "SwiggyMoney";
    paymentInformation: SwiggyAddMoneyPlaceParamsPaymentInfo[];
    // TODO - Cleanup required after feature
    receiverMeta?: GiftReceiverDetails;
    // TODO - Cleanup required after feature
    creditType?: string;
}

export enum ORDER_TYPE {
    SUBSCRIPTION = "subscription",
}

export interface PendingPaymentResponseParams {
    host: string;
    method: string;
    endpoint: string;
    headers: { [key: string]: string };
    body?: { [key: string]: unknown };
    queryParams?: { [key: string]: unknown };
    isConfirmOnAppLaunchEnabled?: boolean;
}

export interface DashOrderResponse {
    orders: Array<{
        orderJobs: DashOrderJob[];
    }>;
    paymentDetails: {
        appRedirectionInfo: string; // this is a json string, we json.parse it on client. checkout systems act as passthrough so they dont touch this key/value
        targetedMarketplacePaymentForwardReferenceId: string;
    };
    transactionPaymentDetails: Array<{
        paymentTxnStatus: string;
        amazonPayResponse?: {
            amazonPayLoad: Record<string, string>;
        };
        paymentMethodMeta?: unknown;
        paymentPartnerOrderId?: string;
        paymentTxnId?: string;
        confirmationTimeInMinutes?: string;
    }>;
    xpData?: {
        confirmOrderEnabled?: boolean;
    };
    deliveryAddress?: DeliveryAddress;
    orderTransitionScreenNewMsgEnabled?: boolean;
}

export interface DashOrderJob {
    extendedStatus?: string; // only applicable for DO orders
    status: string;
    metadata: string;
    orderJobId: string;
    paymentInfo: Array<{
        paymentMethod: string;
        transactionAmount: number;
    }> | null;
}

export interface OrderPlaceParams {
    addressId: string | number;
    paymentMethod: string;
    orderType?: string;
    orderComments?: string;
    proceedWithDuplicateOrder?: boolean;
    useJuspayNative?: boolean;
    paymentReturnUrl?: string;
    marketplaceBusinessLine?: string;
    cartType?: string;
    paymentAmount?: string | number;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    transaction_amount?: string | number;
    [key: string]: unknown;
    giftDetails?: GiftDetails;
}

export interface ConfirmOrderPublicParams {
    transactionId: string;
    orderId: string | number;
    marketplaceBusinessLine?: MARKETPLACE_BUSINESS_LINE;
    giftDetails?: GiftDetails;
    confirmOrderXp?: boolean;
    [key: string]: unknown;
}

export interface PlaceOrderPublicParams {
    addressId: string | number;
    paymentMethod: string;
    orderType?: string;
    orderComments?: string;
    proceedWithDuplicateOrder?: boolean;
    useJuspayNative?: boolean;
    paymentReturnUrl?: string;
    marketplaceBusinessLine?: MARKETPLACE_BUSINESS_LINE;
    [key: string]: unknown;
    giftDetails?: GiftDetails;
}
